import { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import CheckboxSetting from '../atoms/CheckboxSetting';
import GeneralCodesEnum from '@settings/models/general-codes.enum';
import JNumberField from '@atoms/JNumberField';
import JTextFieldBase from '@atoms/JTextFieldBase';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import NumberSetting from '../atoms/NumberSetting';
import UpdateSettingDto from '@settings/models/dto/update-setting.dto';
import { useGetGeneralSettings } from '@settings/queries/useGetSettings';
import useJConfirmationBar from '@/hooks/useJConfirmationBar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUpdateSettings } from '@settings/mutations/settingsMutations';

const GeneralSettings = () => {
  const { data, isFetching: isSettingsFetching } = useGetGeneralSettings();
  const { t } = useTranslation();
  const {
    mutate: mutateSettings,
    isLoading: isMutateSettingsLoading,
    isSuccess: isMutateSettingsSuccess,
  } = useUpdateSettings();
  const { show, close } = useJConfirmationBar();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [payload, setPayload] = useState<UpdateSettingDto[]>([]);
  const settings = data?.data;
  const getValueByCode = (code: GeneralCodesEnum): string | undefined => {
    return settings?.find((item) => item.code === code)?.value;
  };

  const getDefaultValueByCode = (
    code: GeneralCodesEnum,
  ): string | undefined => {
    return settings?.find((item) => item.code === code)?.defaultValue;
  };

  const onChangeSetting = (code: GeneralCodesEnum, value: string) => {
    // set is dirty
    if (!isDirty) {
      setIsDirty(true);
    }
    // Check if the code already exists in payload
    const existingIndex = payload.findIndex((item) => item.code === code);
    if (existingIndex !== -1) {
      updateValue(code, value, existingIndex);
    } else {
      addValue(code, value);
    }
  };

  const addValue = (code: GeneralCodesEnum, value: string) => {
    setPayload((prev) => [...prev, { code, value }]);
  };
  const updateValue = (
    code: GeneralCodesEnum,
    value: string,
    index: number,
  ) => {
    const updatedPayload = [...payload];
    updatedPayload[index] = { code, value };
    setPayload(updatedPayload);
  };

  useEffect(() => {
    if (!isDirty) return;
    show({
      onClose: () => {
        queryClient.invalidateQueries(['settings-general']);
      },
      onConfirm: () => mutateSettings({ payload }),
    });
  }, [isDirty, payload]);

  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    if (!isMutateSettingsSuccess) return;
    close();
  }, [isMutateSettingsSuccess]);

  const isFieldHidden = useCallback(
    (label: string, rank: number): boolean => {
      if (rank.toLocaleString() === searchText) return false;
      return Boolean(
        searchText &&
          !label.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()),
      );
    },
    [searchText],
  );
  const addressRueHidden = isFieldHidden(t('ADDRESS_RUE'), 1);
  const additionalStreetHidden = isFieldHidden(t('ADDITIONAL_STREET'), 2);
  const addressZipHidden = isFieldHidden(t('ADDRESS_ZIP'), 3);
  const addressCityHidden = isFieldHidden(t('ADDRESS_CITY'), 4);
  const autoCompleteHidden = isFieldHidden(t('AUTO_COMPLETE'), 5);
  const cvVideoNotificationHidden = isFieldHidden(
    t('CV_VIDEO_NOTIFICATION'),
    6,
  );
  const applicationNotificationHidden = isFieldHidden(
    t('APPLICATION_NOTIFICATION'),
    7,
  );
  const jobNotificationHidden = isFieldHidden(t('JOB_NOTIFICATION'), 8);

  const displayAddressSettings = !(
    additionalStreetHidden &&
    addressRueHidden &&
    addressCityHidden &&
    addressZipHidden
  );

  const displayOtherSettings = !(
    autoCompleteHidden &&
    applicationNotificationHidden &&
    jobNotificationHidden &&
    cvVideoNotificationHidden
  );
  return (
    <>
      {(isMutateSettingsLoading || isSettingsFetching) ?? <Loading />}
      <Box mb={3}>
        <JTextFieldBase
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          attr={{ whiteBg: true }}
          label={t('SEARCH_SETTING')}
        />
      </Box>
      {!displayAddressSettings && !displayOtherSettings && (
        <Jpaper>{t('NOT_SETTINGS_FOUND')}</Jpaper>
      )}
      {displayAddressSettings && (
        <Jpaper mb={3}>
          <NumberSetting
            hidden={addressRueHidden}
            label={t('ADDRESS_RUE')}
            rank={1}
          >
            <JNumberField
              size="medium"
              name="ADDRESS_RUE"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.AddressStreetMaxChars,
              )}
              newValue={getValueByCode(GeneralCodesEnum.AddressStreetMaxChars)}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.AddressStreetMaxChars,
                  String(value),
                );
              }}
              label={t('MAX_CHARS')}
              reset={isSettingsFetching}
            />
          </NumberSetting>

          <NumberSetting
            hidden={additionalStreetHidden}
            label={t('ADDITIONAL_STREET')}
            rank={2}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="ADDITIONAL_STREET"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.MaxAddressAdditionalStreetMaxChars,
              )}
              newValue={getValueByCode(
                GeneralCodesEnum.MaxAddressAdditionalStreetMaxChars,
              )}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.MaxAddressAdditionalStreetMaxChars,
                  String(value),
                );
              }}
              label={t('MAX_CHARS')}
            />
          </NumberSetting>

          <NumberSetting
            hidden={addressZipHidden}
            label={t('ADDRESS_ZIP')}
            rank={3}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.AddressZipcodeMaxChars,
              )}
              newValue={getValueByCode(GeneralCodesEnum.AddressZipcodeMaxChars)}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.AddressZipcodeMaxChars,
                  String(value),
                );
              }}
              label={t('MAX_CHARS')}
            />
          </NumberSetting>

          <NumberSetting
            hidden={addressCityHidden}
            caption={
              t('ADDRESS_AUTOCMPLETE_HIT', {
                charsCount: getValueByCode(
                  GeneralCodesEnum.AddressCityAutocompleteMinChars,
                ),
                optionsCount: getValueByCode(
                  GeneralCodesEnum.AddressCityOptionsMaxCount,
                ),
              }) ?? ''
            }
            rank={4}
            label={t('ADDRESS_CITY')}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.AddressCityAutocompleteMinChars,
              )}
              newValue={getValueByCode(
                GeneralCodesEnum.AddressCityAutocompleteMinChars,
              )}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.AddressCityAutocompleteMinChars,
                  String(value),
                );
              }}
              label={t('MAX_CHARS')}
            />
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.AddressCityOptionsMaxCount,
              )}
              newValue={getValueByCode(
                GeneralCodesEnum.AddressCityOptionsMaxCount,
              )}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.AddressCityOptionsMaxCount,
                  String(value),
                );
              }}
              label={'Options'}
            />
          </NumberSetting>
        </Jpaper>
      )}
      {displayOtherSettings && (
        <Jpaper>
          <NumberSetting
            hidden={autoCompleteHidden}
            rank={5}
            label={t('AUTO_COMPLETE')}
            caption={
              t('AUTO_COMPLETE_HIT', {
                charsCount: getValueByCode(
                  GeneralCodesEnum.AutocompleteMinChars,
                ),
              }) ?? ''
            }
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                GeneralCodesEnum.AutocompleteMinChars,
              )}
              newValue={getValueByCode(GeneralCodesEnum.AutocompleteMinChars)}
              onNumberChange={(value) => {
                onChangeSetting(
                  GeneralCodesEnum.AutocompleteMinChars,
                  String(value),
                );
              }}
              label={t('MAX_CHARS')}
            />
          </NumberSetting>

          <CheckboxSetting
            hidden={cvVideoNotificationHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(
                GeneralCodesEnum.NotificationCvVideoStatus,
              ) ?? 'true'
            }
            newValue={getValueByCode(
              GeneralCodesEnum.NotificationCvVideoStatus,
            )}
            rank={6}
            label={t('CV_VIDEO_NOTIFICATION')}
            items={[
              { label: t('ENABLEDS'), value: 'true' },
              { label: t('DISABLEDS'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                GeneralCodesEnum.NotificationCvVideoStatus,
                String(value),
              )
            }
          />

          <CheckboxSetting
            hidden={applicationNotificationHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(
                GeneralCodesEnum.NotificationApplicationStatus,
              ) ?? 'true'
            }
            newValue={getValueByCode(
              GeneralCodesEnum.NotificationApplicationStatus,
            )}
            rank={7}
            label={t('APPLICATION_NOTIFICATION')}
            items={[
              { label: t('ENABLEDS'), value: 'true' },
              { label: t('DISABLEDS'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                GeneralCodesEnum.NotificationApplicationStatus,
                String(value),
              )
            }
          />

          <CheckboxSetting
            hidden={jobNotificationHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(GeneralCodesEnum.NotificationJobStatus) ??
              'true'
            }
            newValue={getValueByCode(GeneralCodesEnum.NotificationJobStatus)}
            rank={8}
            label={t('JOB_NOTIFICATION')}
            items={[
              { label: t('ENABLEDS'), value: 'true' },
              { label: t('DISABLEDS'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                GeneralCodesEnum.NotificationJobStatus,
                String(value),
              )
            }
          />
        </Jpaper>
      )}
    </>
  );
};

export default GeneralSettings;
